import type { ViewStyle } from 'react-native'
import { useEffect } from 'react'
import { Platform } from 'react-native'
import {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'

const HERO_MIN_HEIGHT_NATIVE = 180
const HERO_MIN_HEIGHT_WEB = 112
const HERO_OVERLAP = 24

export function usePurchaseScreenAnimation({
  cardHeight,
  isCompactLayout,
  isVisible,
  windowHeight,
}: {
  cardHeight: number
  isCompactLayout: boolean
  isVisible: boolean
  windowHeight: number
}) {
  const cardHiddenOffset = Math.max(windowHeight * 0.2, 120)
  const cardTranslateY = useSharedValue(cardHiddenOffset)
  const cardOpacity = useSharedValue(0)
  const heroMinHeight =
    isCompactLayout && Platform.OS === 'web'
      ? HERO_MIN_HEIGHT_WEB
      : HERO_MIN_HEIGHT_NATIVE
  const defaultHeroHeight =
    isCompactLayout && Platform.OS === 'web'
      ? Math.min(Math.max(windowHeight * 0.29, heroMinHeight), 240)
      : Math.min(Math.max(windowHeight * 0.45, 260), 420)
  const maxHeroHeightForContent =
    cardHeight > 0
      ? windowHeight - cardHeight + HERO_OVERLAP
      : defaultHeroHeight
  const heroHeight = Math.max(
    heroMinHeight,
    Math.min(defaultHeroHeight, maxHeroHeightForContent),
  )

  useEffect(() => {
    if (!isVisible) {
      cardTranslateY.set(cardHiddenOffset)
      cardOpacity.set(0)
      return
    }

    cardTranslateY.set(cardHiddenOffset)
    cardOpacity.set(0)
    const animationFrame = requestAnimationFrame(() => {
      cardTranslateY.set(
        withSpring(0, {
          damping: 20,
          stiffness: 190,
          mass: 0.9,
          velocity: 6,
          overshootClamping: false,
        }),
      )
      cardOpacity.set(
        withTiming(1, {
          duration: 220,
          easing: Easing.out(Easing.cubic),
        }),
      )
    })

    return () => {
      cancelAnimationFrame(animationFrame)
    }
  }, [cardHiddenOffset, cardOpacity, cardTranslateY, isVisible])

  const cardAnimatedStyle = useAnimatedStyle<ViewStyle>(() => ({
    opacity: cardOpacity.get(),
    transform: [{ translateY: cardTranslateY.get() }],
  }))

  return {
    cardAnimatedStyle,
    heroHeight,
  }
}
